import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import BoxedTitle from "@/components/BoxedTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "vbb_admin_key";

const THREAT_STYLES = {
  high: "bg-[#e34234] text-white",
  medium: "bg-[#0a0a0a] text-white",
  low: "bg-white text-[#0a0a0a] border border-[#0a0a0a]",
};

const THREAT_LABEL = {
  high: "High threat",
  medium: "Watch",
  low: "Low",
};

function slugify(name) {
  return name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
}

export default function CompetitorDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || "");
  const [competitors, setCompetitors] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [savedState, setSavedState] = useState("idle"); // idle | saving | saved | error
  const [notesDraft, setNotesDraft] = useState("");

  useEffect(() => {
    if (!key) {
      navigate(`/login?next=/admin/market/competitors/${slug}`, { replace: true });
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/admin/market/competitors`, {
        headers: { "X-Admin-Key": key },
      })
      .then((res) => {
        if (cancelled) return;
        if (!sessionStorage.getItem(KEY_STORAGE)) return;
        setCompetitors(res.data || []);
        setStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          sessionStorage.removeItem(KEY_STORAGE);
          setKey("");
          navigate(`/login?next=/admin/market/competitors/${slug}`, { replace: true });
        } else {
          setStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [key, navigate, slug]);

  const competitor = useMemo(
    () => competitors.find((c) => slugify(c.name) === slug),
    [competitors, slug]
  );

  // Sync notes draft when competitor loads/changes
  useEffect(() => {
    if (competitor) setNotesDraft(competitor.notes || "");
  }, [competitor]);

  const handleSaveNotes = async () => {
    if (!competitor) return;
    setSavedState("saving");
    try {
      const res = await axios.patch(
        `${API}/admin/market/competitors/${competitor.id}`,
        { notes: notesDraft },
        { headers: { "X-Admin-Key": key } }
      );
      setCompetitors((cs) => cs.map((c) => (c.id === competitor.id ? res.data : c)));
      setSavedState("saved");
      setTimeout(() => setSavedState("idle"), 1800);
    } catch {
      setSavedState("error");
    }
  };

  if (!key) return null;

  if (status === "loading") {
    return (
      <main
        className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-32"
        data-testid="competitor-detail-loading"
      >
        <p className="font-mono-tiny text-[#6b6b6b]">Loading…</p>
      </main>
    );
  }

  if (!competitor) {
    return (
      <main
        className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-32"
        data-testid="competitor-detail-not-found"
      >
        <div className="vbb-overline">404</div>
        <h1 className="font-display uppercase text-5xl sm:text-6xl mt-6 tracking-tighter pl-[0.35em]">
          <BoxedTitle size="lg" thick>
            Unknown
          </BoxedTitle>{" "}
          competitor.
        </h1>
        <p className="mt-6 text-[15px] text-[#2a2a2a]">
          That competitor isn't in the tracker.{" "}
          <Link to="/admin/market/competitors" className="vbb-link">
            Back to the list
          </Link>
          .
        </p>
      </main>
    );
  }

  const dirty = notesDraft !== (competitor.notes || "");

  return (
    <main
      className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-32"
      data-testid="competitor-detail-page"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="vbb-overline">
            <Link to="/admin" className="vbb-link" data-testid="crumb-admin">
              Admin
            </Link>{" "}
            /{" "}
            <Link to="/admin/market" className="vbb-link" data-testid="crumb-market">
              Market
            </Link>{" "}
            /{" "}
            <Link
              to="/admin/market/competitors"
              className="vbb-link"
              data-testid="crumb-competitors"
            >
              Competitors
            </Link>{" "}
            / {competitor.name}
          </div>
          <h1
            className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl mt-6 tracking-tighter leading-[0.95] pl-[0.35em]"
            data-testid="competitor-detail-title"
          >
            <BoxedTitle size="lg" thick>
              {competitor.name}
            </BoxedTitle>
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 font-mono-tiny text-[#6b6b6b]">
            <span>{competitor.base}</span>
            {competitor.url && (
              <a
                href={competitor.url}
                target="_blank"
                rel="noopener noreferrer"
                className="vbb-link"
                data-testid="competitor-external-url"
              >
                {competitor.url.replace(/^https?:\/\//, "")} ↗
              </a>
            )}
            <span
              className={`px-3 py-1 uppercase tracking-wider ${THREAT_STYLES[competitor.threat] || THREAT_STYLES.medium}`}
              data-testid="competitor-detail-threat"
            >
              {THREAT_LABEL[competitor.threat] || competitor.threat}
            </span>
          </div>
        </div>
        <Link
          to="/admin/market/competitors"
          className="vbb-btn vbb-btn-ghost"
          data-testid="competitor-detail-back"
        >
          ← All competitors
        </Link>
      </div>

      {/* PITCH — featured callout */}
      {competitor.pitch && (
        <section
          className="mt-12 border-l-4 border-[#e34234] bg-white p-8 lg:p-10"
          data-testid="competitor-pitch"
        >
          <div className="vbb-overline text-[#e34234]">VBB pitch</div>
          <p className="mt-4 text-[18px] lg:text-[20px] leading-relaxed text-[#0a0a0a]">
            {competitor.pitch}
          </p>
        </section>
      )}

      {/* Detail grid */}
      <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-8">
          <section data-testid="competitor-positioning">
            <div className="vbb-overline">Positioning</div>
            <p className="mt-2 text-[16px] leading-relaxed text-[#2a2a2a]">
              {competitor.positioning}
            </p>
          </section>

          {competitor.stack && (
            <section data-testid="competitor-stack">
              <div className="vbb-overline">Stack</div>
              <p className="mt-2 text-[15px] leading-relaxed text-[#2a2a2a]">
                {competitor.stack}
              </p>
            </section>
          )}

          {competitor.pricing && (
            <section data-testid="competitor-pricing">
              <div className="vbb-overline">Pricing</div>
              <p className="mt-2 text-[15px] leading-relaxed text-[#2a2a2a]">
                {competitor.pricing}
              </p>
            </section>
          )}

          {competitor.notable_clients && (
            <section data-testid="competitor-clients">
              <div className="vbb-overline">Notable clients</div>
              <p className="mt-2 text-[15px] leading-relaxed text-[#2a2a2a]">
                {competitor.notable_clients}
              </p>
            </section>
          )}
        </div>

        <aside className="lg:col-span-5 space-y-8">
          {competitor.strengths?.length > 0 && (
            <section data-testid="competitor-strengths">
              <div className="vbb-overline">Strengths</div>
              <ul className="mt-2 space-y-2">
                {competitor.strengths.map((s) => (
                  <li
                    key={s}
                    className="text-[15px] leading-relaxed text-[#2a2a2a]"
                  >
                    — {s}
                  </li>
                ))}
              </ul>
            </section>
          )}
          {competitor.weaknesses?.length > 0 && (
            <section data-testid="competitor-weaknesses">
              <div className="vbb-overline text-[#e34234]">
                Where VBB attacks
              </div>
              <ul className="mt-2 space-y-2">
                {competitor.weaknesses.map((w) => (
                  <li
                    key={w}
                    className="text-[15px] leading-relaxed text-[#2a2a2a]"
                  >
                    — {w}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </aside>
      </div>

      {/* Notes */}
      <section
        className="mt-16 border-t border-[#0a0a0a] pt-8"
        data-testid="competitor-detail-notes-section"
      >
        <label
          htmlFor="competitor-notes"
          className="vbb-overline block"
        >
          Your notes
        </label>
        <textarea
          id="competitor-notes"
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
          rows={5}
          className="mt-3 w-full border border-[#0a0a0a] p-4 font-mono-tiny text-[13px] resize-y focus:outline-none focus:border-[#e34234]"
          placeholder="Internal notes — outreach attempts, learnings, pricing intel, who responded…"
          data-testid="competitor-detail-notes-input"
        />
        <div className="mt-3 flex items-center justify-between">
          <span
            className="font-mono-tiny text-[#6b6b6b]"
            data-testid="competitor-detail-save-state"
          >
            {savedState === "saving" && "Saving…"}
            {savedState === "saved" && "Saved."}
            {savedState === "error" && (
              <span className="text-[#e34234]">Save failed.</span>
            )}
          </span>
          <button
            type="button"
            onClick={handleSaveNotes}
            disabled={!dirty || savedState === "saving"}
            className="vbb-btn disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="competitor-detail-save-btn"
          >
            Save notes
          </button>
        </div>
      </section>
    </main>
  );
}
